import { useState } from "react";
import { Form, Formik } from "formik";
import Card from "../UI/Card";
import Callout from "../UI/Callout";
import Text from "../UI/Text";
import Title from "../UI/Title";
// @ts-expect-error pure js
import ToggleSwitch from "../UI/ToggleSwitch";
import Smtp from "../Account/Smtp";
import MailSignature from "../Account/MailSignature";
import Logo from "../Account/Logo";
import Signature from "../Account/Signature";
import PennylaneCard from "./PennylaneCard";
import ProfileAccountingSettings from "./ProfileAccountingSettings";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import TextArea from "../UI/TextArea";
import FormikSubmitButton from "../UI/FormikSubmitButton";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import useCustomization from "../../hooks/use-customization";
// @ts-expect-error pure js
import useNumerotation from "../../hooks/use-numerotation";
// @ts-expect-error pure js
import { classnames, handleAPIError } from "../../utils";
import type { ProfileBailleur } from "./types";
import styles from "./ParametreProfileBailleur.module.css";

const DEFAULT_CONDITIONS_REGLEMENT = `Des pénalités correspondant au taux directeur de la BCE, majoré de 10 points seront applicables en l'absence ou en cas de retard de paiement.
Les pénalités de retard sont exigibles sans qu'un rappel soit nécessaire.
Une indemnité forfaitaire de 40€ pour frais de recouvrement sera appliquée en cas de retard de paiement.`;

const AUTO_ACTION_NOTIFICATION_OPTIONS = [
  { value: "mail", label: "Par mail" },
  { value: "dashboard", label: "Sur le tableau de bord" },
];

interface ParametreProfileBailleurProps {
  profileBailleur: ProfileBailleur;
}

interface FacturationFormValues {
  echeance_paiement_defaut: string;
  conditions_reglement: string;
  prefix: string;
  numero: string;
}

export default function ParametreProfileBailleur({
  profileBailleur,
}: ParametreProfileBailleurProps) {
  const { updateProfileBailleur } = useProfileBailleurs();
  const {
    customization,
    createOrUpdateCustomization,
    resetCustomization,
    testSmtp,
  } = useCustomization(profileBailleur.id);
  const { numerotation, updateNumerotation } = useNumerotation(
    profileBailleur.id,
  );
  const [facturationError, setFacturationError] = useState("");
  const [facturationSuccess, setFacturationSuccess] = useState("");

  const shouldCc = profileBailleur.should_cc ?? true;
  const shouldSendRecap = profileBailleur.should_send_recap ?? true;
  const autoActionNotification: string[] =
    customization?.auto_action_notification ?? ["mail", "dashboard"];

  async function handleToggleNotificationChannel(channel: string) {
    const channels = autoActionNotification.includes(channel)
      ? autoActionNotification.filter((c) => c !== channel)
      : [...autoActionNotification, channel];
    try {
      await createOrUpdateCustomization({ auto_action_notification: channels });
    } catch (e) {
      console.error(e);
    }
  }

  async function handleToggleCcBailleur() {
    try {
      await updateProfileBailleur(profileBailleur.id, { should_cc: !shouldCc });
    } catch (e) {
      console.error(e);
    }
  }

  async function handleToggleSendRecap() {
    try {
      await updateProfileBailleur(profileBailleur.id, {
        should_send_recap: !shouldSendRecap,
      });
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <div className={styles.wrapper}>
      <Signature
        customization={customization}
        onSave={createOrUpdateCustomization}
        onReset={resetCustomization}
        isProfile
      />
      <Logo
        customization={customization}
        onSave={createOrUpdateCustomization}
        onReset={resetCustomization}
        isProfile
      />
      <MailSignature
        customization={customization}
        onSave={createOrUpdateCustomization}
        onReset={resetCustomization}
        isProfile
      />
      <Smtp
        customization={customization}
        onSave={createOrUpdateCustomization}
        onReset={resetCustomization}
        onTest={testSmtp}
        isProfile
      />
      <PennylaneCard profileBailleur={profileBailleur} />
      <ProfileAccountingSettings profileBailleur={profileBailleur} />
      <Card>
        <div className={styles.header}>
          <Title variant="hecto" weight="medium">
            Paramètres des emails
          </Title>
          <Text color="muted">
            Configurez les préférences d'envoi des emails pour ce profil
          </Text>
        </div>

        <div className={styles.settingRow}>
          <div className={styles.settingInfo}>
            <Text weight="medium">Copie des emails au bailleur</Text>
            <Text variant="deci" color="muted">
              Le bailleur sera mis en copie (CC) des emails automatique envoyés
              aux locataires
            </Text>
          </div>
          <ToggleSwitch
            status={shouldCc}
            onToggle={handleToggleCcBailleur}
            requiresWrite
          />
        </div>

        <div className={styles.settingRow}>
          <div className={styles.settingInfo}>
            <Text weight="medium">Récapitulatif des emails</Text>
            <Text variant="deci" color="muted">
              Le bailleur recevra un récapitulatif des emails programmés
            </Text>
          </div>
          <ToggleSwitch
            status={shouldSendRecap}
            onToggle={handleToggleSendRecap}
            requiresWrite
          />
        </div>

        <div className={styles.sectionHeading}>
          <Text weight="medium">Notifications des actions automatiques</Text>
          <Text variant="deci" color="muted">
            Recevez les notifications des automatisations par mail et/ou dans
            vos actions prioritaires sur le tableau de bord
          </Text>
        </div>
        {AUTO_ACTION_NOTIFICATION_OPTIONS.map((option) => (
          <div
            key={option.value}
            className={classnames(styles.settingRow, styles.indented)}
          >
            <Text weight="medium">{option.label}</Text>
            <ToggleSwitch
              status={autoActionNotification.includes(option.value)}
              onToggle={() => handleToggleNotificationChannel(option.value)}
              requiresWrite
            />
          </div>
        ))}
      </Card>

      <Card>
        <div className={styles.header}>
          <Title variant="hecto" weight="medium">
            Paramètres de facturation
          </Title>
          <Text color="muted">
            Configurez les paramètres de facturation pour les locataires de type
            "Facture"
          </Text>
        </div>
        <Formik<FacturationFormValues>
          enableReinitialize
          initialValues={{
            echeance_paiement_defaut:
              profileBailleur.echeance_paiement_defaut ||
              "30 jours à réception",
            conditions_reglement:
              profileBailleur.conditions_reglement ||
              DEFAULT_CONDITIONS_REGLEMENT,
            prefix: numerotation?.prefix || "FAC",
            numero: String(numerotation?.numero ?? 0),
          }}
          onSubmit={async (values, { setSubmitting }) => {
            setFacturationError("");
            setFacturationSuccess("");
            try {
              await updateProfileBailleur(profileBailleur.id, {
                echeance_paiement_defaut:
                  values.echeance_paiement_defaut || null,
                conditions_reglement: values.conditions_reglement || null,
              });

              await updateNumerotation({
                prefix: values.prefix,
                numero: parseInt(values.numero) || 0,
              });

              setFacturationSuccess("Paramètres de facturation enregistrés");
            } catch (e) {
              setFacturationError(handleAPIError(e));
            }
            setSubmitting(false);
          }}
        >
          {({ values }) => (
            <Form className={styles.form}>
              <div className={styles.billingRow}>
                <TextInput
                  name="prefix"
                  label="Préfixe des factures"
                  placeholder="FAC"
                  className={styles.grow}
                  marginForm=""
                />
                <TextInput
                  name="numero"
                  label="Dernier numéro utilisé"
                  type="number"
                  placeholder="0"
                  className={styles.grow}
                  marginForm=""
                />
              </div>
              <Text variant="deci">
                La prochaine facture sera numérotée :{" "}
                <Text as="span" variant="deci" weight="bold">
                  {values.prefix || "FAC"}
                  {String((parseInt(values.numero) || 0) + 1).padStart(6, "0")}
                </Text>
              </Text>

              <TextInput
                name="echeance_paiement_defaut"
                label="Échéance de paiement"
                placeholder="30 jours à réception"
                marginForm=""
              />

              <TextArea
                name="conditions_reglement"
                label="Conditions de règlement"
                rows={5}
                marginForm=""
              />
              <Callout variant="danger">{facturationError}</Callout>
              <Callout variant="success">{facturationSuccess}</Callout>

              <FormikSubmitButton requiresWrite>Enregistrer</FormikSubmitButton>
            </Form>
          )}
        </Formik>
      </Card>
    </div>
  );
}
